import type { RealtimeChannel } from "@supabase/supabase-js";
import { getClient } from "./client.js";
import { mergeCards, mergePrefs, mergePlans, type RemoteCard, type RemotePref, type RemotePlan } from "./conflict.js";
import { isAbandonedCardId } from "@music-app/learning-system";
import { useSyncStore } from "./sync-store.js";
import {
  loadAllCardRecords,
  loadAllPreferences,
  loadReviewsSince,
  loadActivitySessionsSince,
  loadItemEventsSince,
  loadAllPlanRecords,
  getDb,
  useCardsStore,
  usePrefsStore,
  useSavedPlansStore,
  type ReviewRecord,
  type ActivitySessionRecord,
  type ItemEventRecord,
} from "@music-app/persistence";

const LAST_SYNC_KEY = "sync:lastSyncAt";
const PUSH_DEBOUNCE_MS = 5_000;
const MAX_SYNC_ATTEMPTS = 4; // 1 initial + 3 retries

export class SyncEngine {
  private readonly userId: string;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private realtimeChannel: RealtimeChannel | null = null;

  constructor(userId: string) {
    this.userId = userId;
  }

  schedulePush(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => void this.sync(), PUSH_DEBOUNCE_MS);
  }

  async sync(): Promise<void> {
    const supabase = getClient();
    if (!supabase) return;

    useSyncStore.getState().setSyncing(true);

    try {
      await this._syncWithRetry();
      useSyncStore.getState().setError(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      useSyncStore.getState().setError(msg);
      console.warn("[sync]", msg);
    } finally {
      useSyncStore.getState().setSyncing(false);
    }
  }

  private async _syncWithRetry(): Promise<void> {
    let lastErr: unknown;
    for (let attempt = 0; attempt < MAX_SYNC_ATTEMPTS; attempt++) {
      try {
        await this._doSync();
        return;
      } catch (err) {
        lastErr = err;
        if (attempt + 1 >= MAX_SYNC_ATTEMPTS) break;
        const msg = err instanceof Error ? err.message : String(err);
        if (/\b(401|403|unauthorized|forbidden)\b/i.test(msg)) break;
        const base = Math.min(1_000 * 2 ** attempt, 30_000);
        const delay = base * (0.8 + Math.random() * 0.4);
        await new Promise<void>((resolve) => setTimeout(resolve, delay));
      }
    }
    throw lastErr;
  }

  private async _doSync(): Promise<void> {
    const supabase = getClient();
    if (!supabase) return;

    const now = new Date().toISOString();
    const lastSyncAt = localStorage.getItem(LAST_SYNC_KEY) ?? "1970-01-01T00:00:00.000Z";

    // ── 1. FETCH REMOTE ────────────────────────────────────────────────
    const [cardsRes, prefsRes, reviewsRes, activityRes, eventsRes, plansRes] = await Promise.all([
      supabase
        .from("cards")
        .select("item_id, card, updated_at")
        .eq("user_id", this.userId),
      supabase
        .from("preferences")
        .select("key, value, updated_at")
        .eq("user_id", this.userId),
      supabase
        .from("reviews")
        .select("local_id, item_id, session_id, rating, correct, response_ms, created_at, chosen_answer_id, source")
        .eq("user_id", this.userId)
        .gt("created_at", lastSyncAt),
      supabase
        .from("activity_sessions")
        .select("id, session_type, node_id, skill_id, started_at, ended_at, item_count, metadata")
        .eq("user_id", this.userId)
        .gt("started_at", lastSyncAt),
      supabase
        .from("item_events")
        .select("id, session_id, item_id, node_id, skill_id, event_type, played_at, metadata")
        .eq("user_id", this.userId)
        .gt("played_at", lastSyncAt),
      supabase
        .from("saved_plans")
        .select("id, name, source, generator_id, session_mix, focus_tracks, focus_node_ids, config_overrides, generator_state, created_at, updated_at")
        .eq("user_id", this.userId),
    ]);

    if (cardsRes.error) throw new Error(cardsRes.error.message);
    if (prefsRes.error) throw new Error(prefsRes.error.message);
    if (reviewsRes.error) throw new Error(reviewsRes.error.message);
    if (activityRes.error) throw new Error(activityRes.error.message);
    if (eventsRes.error) throw new Error(eventsRes.error.message);
    if (plansRes.error) throw new Error(plansRes.error.message);

    const remoteCards = cardsRes.data as RemoteCard[];
    const remotePrefs = prefsRes.data as RemotePref[];
    const remoteNewReviews = reviewsRes.data;
    const remoteNewActivity = activityRes.data;
    const remoteNewEvents = eventsRes.data;
    const remotePlans = plansRes.data as RemotePlan[];

    // ── 2. LOAD LOCAL ─────────────────────────────────────────────────
    const [localCards, localPrefs, localNewReviews, localNewActivity, localNewEvents, localPlans] =
      await Promise.all([
        loadAllCardRecords(),
        loadAllPreferences(),
        loadReviewsSince(lastSyncAt),
        loadActivitySessionsSince(lastSyncAt),
        loadItemEventsSince(lastSyncAt),
        loadAllPlanRecords(),
      ]);

    // ── 3. MERGE ──────────────────────────────────────────────────────
    // Abandoned orphan cards (re-homed free-drill configs) are pruned, not
    // synced: never pull or push them, and delete any found on either side so
    // they stop polluting the forecast. See learning-system/isAbandonedCardId.
    const cardMerge = mergeCards(localCards, remoteCards);
    const cardLocalUpdates = cardMerge.localUpdates.filter((c) => !isAbandonedCardId(c.id));
    const cardRemoteUpdates = cardMerge.remoteUpdates.filter((c) => !isAbandonedCardId(c.id));
    const abandonedLocalIds = localCards.filter((c) => isAbandonedCardId(c.id)).map((c) => c.id);
    const abandonedRemoteIds = remoteCards.filter((c) => isAbandonedCardId(c.item_id)).map((c) => c.item_id);

    const { localUpdates: prefLocalUpdates, remoteUpdates: prefRemoteUpdates } =
      mergePrefs(localPrefs, remotePrefs);

    const { localUpdates: planLocalUpdates, remoteUpdates: planRemoteUpdates } =
      mergePlans(localPlans, remotePlans);

    // Reviews: union — apply remote-only reviews locally, push local-only remotely.
    const localReviewIds = new Set(localNewReviews.map((r) => r.id));
    const reviewsToApplyLocally: ReviewRecord[] = (remoteNewReviews as Array<Record<string, unknown>>)
      .filter((r) => !localReviewIds.has(r["local_id"] as string))
      .map((r) => ({
        id: r["local_id"] as string,
        itemId: r["item_id"] as string,
        sessionId: (r["session_id"] as string | null) ?? "",
        rating: r["rating"] as ReviewRecord["rating"],
        correct: r["correct"] as boolean,
        responseMs: r["response_ms"] as number,
        timestamp: r["created_at"] as string,
        chosenAnswerId: (r["chosen_answer_id"] as string | null) ?? undefined,
        source: (r["source"] as ReviewRecord["source"] | null) ?? undefined,
      }));

    // Activity sessions: union — append-only.
    const localActivityIds = new Set(localNewActivity.map((s) => s.id));
    const activityToApplyLocally: ActivitySessionRecord[] = (
      remoteNewActivity as Array<Record<string, unknown>>
    )
      .filter((r) => !localActivityIds.has(r["id"] as string))
      .map((r) => ({
        id: r["id"] as string,
        sessionType: r["session_type"] as ActivitySessionRecord["sessionType"],
        nodeId: (r["node_id"] as string | null) ?? null,
        skillId: (r["skill_id"] as string | null) ?? null,
        startedAt: r["started_at"] as string,
        endedAt: (r["ended_at"] as string | null) ?? null,
        itemCount: r["item_count"] as number,
        metadata: (r["metadata"] as Record<string, unknown>) ?? {},
      }));

    // Item events: union — append-only.
    const localEventIds = new Set(localNewEvents.map((e) => e.id));
    const eventsToApplyLocally: ItemEventRecord[] = (
      remoteNewEvents as Array<Record<string, unknown>>
    )
      .filter((r) => !localEventIds.has(r["id"] as string))
      .map((r) => ({
        id: r["id"] as string,
        sessionId: r["session_id"] as string,
        itemId: r["item_id"] as string,
        nodeId: r["node_id"] as string,
        skillId: (r["skill_id"] as string | null) ?? null,
        eventType: r["event_type"] as "listen" | "drill",
        playedAt: r["played_at"] as string,
        metadata: (r["metadata"] as Record<string, unknown>) ?? {},
      }));

    // ── 4. APPLY TO LOCAL ─────────────────────────────────────────────
    const db = getDb();
    let cardsDirty = false;
    let prefsDirty = false;

    if (abandonedLocalIds.length > 0) {
      await db.cards.bulkDelete(abandonedLocalIds);
      cardsDirty = true;
    }
    if (cardLocalUpdates.length > 0) {
      await db.cards.bulkPut(cardLocalUpdates);
      cardsDirty = true;
    }
    if (prefLocalUpdates.length > 0) {
      await db.preferences.bulkPut(prefLocalUpdates);
      prefsDirty = true;
    }
    if (reviewsToApplyLocally.length > 0) {
      await db.reviews.bulkPut(reviewsToApplyLocally);
    }
    if (activityToApplyLocally.length > 0) {
      await db.activitySessions.bulkPut(activityToApplyLocally);
    }
    if (eventsToApplyLocally.length > 0) {
      await db.itemEvents.bulkPut(eventsToApplyLocally);
    }
    if (planLocalUpdates.length > 0) {
      await db.savedPlans.bulkPut(planLocalUpdates);
    }

    if (cardsDirty) await useCardsStore.getState().hydrate();
    if (prefsDirty) await usePrefsStore.getState().hydrate();
    if (planLocalUpdates.length > 0) await useSavedPlansStore.getState().hydrate();

    // ── 5. PUSH TO REMOTE ─────────────────────────────────────────────
    if (abandonedRemoteIds.length > 0) {
      const { error } = await supabase
        .from("cards")
        .delete()
        .eq("user_id", this.userId)
        .in("item_id", abandonedRemoteIds);
      if (error) throw new Error(error.message);
    }
    if (cardRemoteUpdates.length > 0) {
      const { error } = await supabase.from("cards").upsert(
        cardRemoteUpdates.map((c) => ({
          user_id: this.userId,
          item_id: c.id,
          card: c.card,
          updated_at: c.updatedAt,
        })),
      );
      if (error) throw new Error(error.message);
    }

    if (prefRemoteUpdates.length > 0) {
      const { error } = await supabase.from("preferences").upsert(
        prefRemoteUpdates.map((p) => ({
          user_id: this.userId,
          key: p.key,
          value: p.value,
          updated_at: p.updatedAt,
        })),
      );
      if (error) throw new Error(error.message);
    }

    if (localNewReviews.length > 0) {
      const { error } = await supabase.from("reviews").upsert(
        localNewReviews.map((r) => ({
          user_id: this.userId,
          local_id: r.id,
          item_id: r.itemId,
          session_id: r.sessionId || null,
          rating: r.rating,
          correct: r.correct,
          response_ms: Math.round(r.responseMs),
          created_at: r.timestamp,
          chosen_answer_id: r.chosenAnswerId ?? null,
          source: r.source ?? null,
        })),
        { onConflict: "user_id,local_id", ignoreDuplicates: true },
      );
      if (error) throw new Error(error.message);
    }

    if (localNewActivity.length > 0) {
      const { error } = await supabase.from("activity_sessions").upsert(
        localNewActivity.map((s) => ({
          id: s.id,
          user_id: this.userId,
          session_type: s.sessionType,
          node_id: s.nodeId ?? null,
          skill_id: s.skillId ?? null,
          started_at: s.startedAt,
          ended_at: s.endedAt ?? null,
          item_count: s.itemCount,
          metadata: s.metadata,
        })),
        { onConflict: "id", ignoreDuplicates: true },
      );
      if (error) throw new Error(error.message);
    }

    if (localNewEvents.length > 0) {
      const { error } = await supabase.from("item_events").upsert(
        localNewEvents.map((e) => ({
          id: e.id,
          user_id: this.userId,
          session_id: e.sessionId,
          item_id: e.itemId,
          node_id: e.nodeId,
          skill_id: e.skillId ?? null,
          event_type: e.eventType,
          played_at: e.playedAt,
          metadata: e.metadata,
        })),
        { onConflict: "id", ignoreDuplicates: true },
      );
      if (error) throw new Error(error.message);
    }

    if (planRemoteUpdates.length > 0) {
      const { error } = await supabase.from("saved_plans").upsert(
        planRemoteUpdates.map((p) => ({
          id: p.id,
          user_id: this.userId,
          name: p.name,
          source: p.source,
          generator_id: p.generatorId,
          session_mix: p.sessionMix,
          focus_tracks: p.focusTracks ?? null,
          focus_node_ids: p.focusNodeIds ?? null,
          config_overrides: p.configOverrides ?? null,
          generator_state: p.generatorState ?? null,
          created_at: p.createdAt,
          updated_at: p.updatedAt,
        })),
        { onConflict: "id" },
      );
      if (error) throw new Error(error.message);
    }

    localStorage.setItem(LAST_SYNC_KEY, now);
    useSyncStore.getState().setLastSyncAt(now);
  }

  startRealtime(): void {
    const supabase = getClient();
    if (!supabase || this.realtimeChannel) return;

    this.realtimeChannel = supabase
      .channel(`cards-${this.userId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "cards",
          filter: `user_id=eq.${this.userId}`,
        },
        () => void this.sync(),
      )
      .subscribe();
  }

  stopRealtime(): void {
    const supabase = getClient();
    if (supabase && this.realtimeChannel) {
      void supabase.removeChannel(this.realtimeChannel);
      this.realtimeChannel = null;
    }
  }

  destroy(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.stopRealtime();
  }
}
